import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} SubscriptionPickerRefs
 * @property {HTMLButtonElement} toggleButton - The button that expands and collapses the options
 * @property {HTMLInputElement} sellingPlanInput - The product form field that carries the chosen selling plan
 */

/**
 * Expands and collapses the subscription options. Collapsed, only the lead option shows: the chosen
 * one, or the first while nothing is chosen. Choosing an option re-selects its variant through the
 * variant picker, which requests the section on the option's selling plan. A click on a variant option
 * returns to a one-time purchase.
 *
 * @extends {Component<SubscriptionPickerRefs>}
 */
class SubscriptionPicker extends Component {
  requiredRefs = ['toggleButton', 'sellingPlanInput'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('click', this.#handleDocumentClick, { signal });
    this.addEventListener('click', this.#handleOptionClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    const container = this.closest('.shopify-section, dialog');
    container?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    container?.addEventListener('click', this.#handleVariantOptionClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Whether the full list of options is showing.
   * @returns {boolean}
   */
  get expanded() {
    return this.refs.toggleButton.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Expands the options when collapsed, and collapses them when expanded.
   */
  toggle() {
    this.#setExpanded(!this.expanded);
  }

  /**
   * Collapses the options to the lead option.
   */
  collapse() {
    this.#setExpanded(false);
  }

  /**
   * Collapses the options and selects the chosen option's variant, so the section re-renders on its plan.
   */
  select() {
    this.collapse();
    this.#syncSellingPlan();

    const input = this.querySelector('input:checked');
    if (!(input instanceof HTMLInputElement)) return;

    const variantInput = this.closest('.shopify-section, dialog')?.querySelector(
      `variant-picker input[data-variant-id="${input.dataset.variantId}"]`
    );
    if (!(variantInput instanceof HTMLInputElement)) return;

    variantInput.checked = true;
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Copies the chosen option's selling plan into the product form, or clears it for a one-time purchase.
   */
  #syncSellingPlan() {
    const input = this.querySelector('input:checked');
    this.refs.sellingPlanInput.value = input instanceof HTMLInputElement ? input.value : '';
  }

  /**
   * @param {boolean} expanded - Whether the options should be expanded.
   */
  #setExpanded(expanded) {
    const wasExpanded = this.expanded;

    // A newly chosen option takes the lead before the collapse, so the toggle is pinned again.
    if (expanded !== wasExpanded) this.#pinToggleCenter();

    this.refs.toggleButton.setAttribute('aria-expanded', String(expanded));

    if (!expanded && wasExpanded) this.#releaseToggleCenter();
  }

  /**
   * Pins the toggle to the lead option, which keeps its size while the others animate around it.
   */
  #pinToggleCenter() {
    const lead = this.querySelector('.subscription-option:has(:checked)') ?? this.querySelector('.subscription-option');
    if (!(lead instanceof HTMLElement)) return;

    this.style.setProperty('--subscription-toggle-center', `${lead.offsetTop + lead.offsetHeight / 2}px`);
  }

  /**
   * Keeps the toggle on the lead option until the options finish collapsing.
   */
  async #releaseToggleCenter() {
    // A re-render mid-collapse cancels the running transitions and starts new ones.
    let animations = this.getAnimations({ subtree: true });
    while (animations.length > 0) {
      await Promise.allSettled(animations.map((animation) => animation.finished));
      animations = this.getAnimations({ subtree: true });
    }
    if (this.expanded) return;

    this.style.removeProperty('--subscription-toggle-center');
  }

  /**
   * Checks the option the product form still carries, or none, and syncs the form to it.
   * @param {string} sellingPlanId - The selling plan to keep.
   * @param {string | undefined} variantId - The variant the kept option delivers.
   */
  #keepChosenOption(sellingPlanId, variantId) {
    for (const input of this.querySelectorAll('input[type="radio"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = Boolean(sellingPlanId) && input.value === sellingPlanId && input.dataset.variantId === variantId;
    }

    this.#syncSellingPlan();
  }

  /**
   * Re-renders the picker for the selected variant. The product form field, not the checked option,
   * holds the latest choice: a variant option clears the field at once and the option on response.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        // A combined listing sibling has already replaced `<main>` and never carries a plan.
        if (detail.morphElementSelector === 'main') {
          this.#keepChosenOption('', undefined);
          return;
        }

        const newPicker = detail.html.querySelector(`subscription-picker[data-block-id="${this.dataset.blockId}"]`);
        if (!newPicker) return;

        const sellingPlanId = this.refs.sellingPlanInput.value;
        const chosen = this.querySelector('input:checked');
        const variantId = chosen instanceof HTMLInputElement ? chosen.dataset.variantId : undefined;

        const isPinned = Boolean(this.style.getPropertyValue('--subscription-toggle-center'));

        morph(this, newPicker, { ...MORPH_OPTIONS, childrenOnly: false });

        this.#keepChosenOption(sellingPlanId, variantId);

        // The morph drops the inline style and collapses the picker without going through #setExpanded.
        if (isPinned) {
          this.#pinToggleCenter();
          this.#releaseToggleCenter();
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn('[subscription-picker] Event promise rejected:', error);
        if (!this.refs.sellingPlanInput.value) this.#keepChosenOption('', undefined);
      });
  };

  /**
   * Returns to a one-time purchase when a variant option is clicked. Only the form field clears here,
   * so the request that follows goes out without a plan; the option clears with the response.
   * @param {MouseEvent} event - The click event.
   */
  #handleVariantOptionClick = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLInputElement) || !target.closest('variant-picker')) return;
    if (target.closest('product-card')) return;

    if (!this.querySelector('input:checked')) return;

    this.collapse();
    this.refs.sellingPlanInput.value = '';

    // The option already selected fires no change, so the one-time price is requested here.
    if (target.dataset.currentChecked === 'true') target.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Expands or collapses the options when the chosen option is clicked again, which fires no change.
   * @param {MouseEvent} event - The click event.
   */
  #handleOptionClick = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    // Click runs before change, so a newly checked option does not match the form field yet.
    const { value } = this.refs.sellingPlanInput;
    if (!value || target.value !== value || target.dataset.variantId !== this.dataset.variantId) return;

    this.toggle();
  };

  /**
   * Collapses the options when a click lands outside the picker.
   * @param {MouseEvent} event - The click event.
   */
  #handleDocumentClick = (event) => {
    if (!this.expanded || !(event.target instanceof Node) || this.contains(event.target)) return;

    this.collapse();
  };

  /**
   * Collapses the options on Escape and returns focus to the toggle.
   * @param {KeyboardEvent} event - The keydown event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.expanded) return;

    this.collapse();
    this.refs.toggleButton.focus();
  };
}

if (!customElements.get('subscription-picker')) {
  customElements.define('subscription-picker', SubscriptionPicker);
}
